/**
 * Size Badge Component
 *
 * Displays quiz/dataset size classification (XS to XXL+) with color coding.
 * Includes tooltip with estimated time and full size label.
 */

import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import {
  calculateQuizSize,
  getSizeBadgeColor,
  getSizeLabel,
  getSizeRange,
  getEstimatedTime,
  type QuizSize,
} from "@/utils/quizSizing";

interface SizeBadgeProps {
  /**
   * Question/feature count to calculate size from
   */
  count: number;
  /**
   * Optional className for custom styling
   */
  className?: string;
  /**
   * Show tooltip on hover (default: true)
   */
  showTooltip?: boolean;
}

export function SizeBadge({ count, className, showTooltip = true }: SizeBadgeProps) {
  const size = calculateQuizSize(count);
  const colorClasses = getSizeBadgeColor(size);

  const badge = (
    <Badge
      variant="outline"
      className={cn(
        "text-xs font-semibold",
        colorClasses,
        className
      )}
    >
      {size}
    </Badge>
  );

  if (!showTooltip) {
    return badge;
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          {badge}
        </TooltipTrigger>
        <TooltipContent>
          <div className="text-xs space-y-1">
            <div className="font-medium">{getSizeLabel(size)}</div>
            <div className="text-muted-foreground">
              {getSizeRange(size)} questions
            </div>
            <div className="text-muted-foreground">
              Estimated time: {getEstimatedTime(count)}
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * Direct size badge variant (when size is pre-calculated)
 */
interface DirectSizeBadgeProps {
  size: QuizSize;
  count: number;
  className?: string;
  showTooltip?: boolean;
}

export function DirectSizeBadge({ size, count, className, showTooltip = true }: DirectSizeBadgeProps) {
  const colorClasses = getSizeBadgeColor(size);

  const badge = (
    <Badge
      variant="outline"
      className={cn(
        "text-xs font-semibold",
        colorClasses,
        className
      )}
    >
      {size}
    </Badge>
  );

  if (!showTooltip) {
    return badge;
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          {badge}
        </TooltipTrigger>
        <TooltipContent>
          <div className="text-xs space-y-1">
            <div className="font-medium">{getSizeLabel(size)}</div>
            <div className="text-muted-foreground">
              {getSizeRange(size)} questions
            </div>
            <div className="text-muted-foreground">
              Estimated time: {getEstimatedTime(count)}
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
